import { isToday } from 'date-fns'
import type { OrderStatus } from '#src/models/orders/constants.ts'
import type { Order } from '#src/models/orders/Order.ts'

/**
 * Status groups for tiered sorting.
 * Orders are first grouped by status, then sorted by priority/due date within each group.
 */
const StatusGroup = {
  /** Active/On-hold orders that need action */
  NeedsAction: 0,
  /** Coordinated/Effectuated/Completed orders */
  Handled: 1,
  /** Draft orders */
  Draft: 2,
  /** Revoked/Entered-in-error orders */
  Inactive: 3,
} as const
type StatusGroup = (typeof StatusGroup)[keyof typeof StatusGroup]

/**
 * Priority tiers within each status group.
 * Lower tier numbers appear first.
 */
const PriorityTier = {
  CriticalOverdue: 0,
  CriticalDueToday: 1,
  CriticalUpcoming: 2,
  HighOverdue: 3,
  HighActive: 4,
  StandardOverdue: 5,
  StandardActive: 6,
} as const
type PriorityTier = (typeof PriorityTier)[keyof typeof PriorityTier]

/**
 * Get the status group for an order.
 */
function getStatusGroup(status: OrderStatus | undefined): StatusGroup {
  if (!status) {
    return StatusGroup.NeedsAction
  }

  if (status === 'active' || status === 'on-hold') {
    return StatusGroup.NeedsAction
  }

  if (
    status === 'coordinated' ||
    status === 'effectuated' ||
    status === 'completed'
  ) {
    return StatusGroup.Handled
  }

  if (status === 'draft') {
    return StatusGroup.Draft
  }

  return StatusGroup.Inactive
}

/**
 * Get the priority tier for an order based on priority and due date.
 */
function getPriorityTier(order: Order): PriorityTier {
  const dueDate = order.dueDate ? new Date(order.dueDate) : null
  const isOverdue = order.isOverdue
  const isDueTodayValue = dueDate ? isToday(dueDate) : false
  const priority = order.priority

  const isCritical =
    priority?.startsWith('stat') || priority?.startsWith('urgent')

  const isHigh = priority?.startsWith('asap')

  if (isCritical && isOverdue) {
    return PriorityTier.CriticalOverdue
  }

  if (isCritical && isDueTodayValue) {
    return PriorityTier.CriticalDueToday
  }

  if (isCritical) {
    return PriorityTier.CriticalUpcoming
  }

  if (isHigh && isOverdue) {
    return PriorityTier.HighOverdue
  }

  if (isHigh) {
    return PriorityTier.HighActive
  }

  if (isOverdue) {
    return PriorityTier.StandardOverdue
  }

  return PriorityTier.StandardActive
}

/**
 * Calculate the overall tier for an order.
 * Returns a number where lower values should appear first in the list.
 */
export function getOrderTier(order: Order): number {
  const statusGroup = getStatusGroup(order.status)
  const priorityTier = getPriorityTier(order)

  return statusGroup * 10 + priorityTier
}

/**
 * Compare two orders within the same tier for secondary sorting.
 *
 * Within-tier sorting rules:
 * - Overdue orders: Sort by how overdue (most overdue first)
 * - Active orders: Sort by due date (soonest first), then creation date
 */
export function compareOrdersWithinTier(
  a: Order,
  b: Order,
  tier: number
): number {
  const priorityTier = tier % 10

  const isOverdueTier =
    priorityTier === PriorityTier.CriticalOverdue ||
    priorityTier === PriorityTier.HighOverdue ||
    priorityTier === PriorityTier.StandardOverdue

  if (isOverdueTier) {
    const aDueDate = a.dueDate ?? ''
    const bDueDate = b.dueDate ?? ''

    if (aDueDate !== bDueDate) {
      return aDueDate.localeCompare(bDueDate)
    }
  } else {
    const aDueDate = a.dueDate ?? '9999-12-31'
    const bDueDate = b.dueDate ?? '9999-12-31'

    if (aDueDate !== bDueDate) {
      return aDueDate.localeCompare(bDueDate)
    }
  }

  const aDate = a.authoredOn ?? ''
  const bDate = b.authoredOn ?? ''
  return aDate.localeCompare(bDate)
}

/**
 * Sort orders using the tiered priority approach.
 * This is the main sorting function that should be used for the default orders list sorting.
 *
 * Orders are sorted by:
 * 1. Status group (active/on-hold > coordinated/effectuated/completed > draft > revoked/error)
 * 2. Priority tier (critical > high > standard, with overdue prioritized)
 * 3. Within tier: by due date and creation date
 */
export function sortOrdersByTier(orders: Order[]): Order[] {
  return orders.slice().sort((a, b) => {
    const tierA = getOrderTier(a)
    const tierB = getOrderTier(b)

    if (tierA !== tierB) {
      return tierA - tierB
    }

    return compareOrdersWithinTier(a, b, tierA)
  })
}
